/**
 * Which document a recurring EDIT's `patchOriginal` is actually writing.
 *
 * `applyRecurringEditPlan` has one `patchOriginal` effect, but the plans that reach it
 * mean two different things:
 *
 *   - a plan that ALSO creates ("this occurrence", or a split at a middle occurrence)
 *     patches the REMAINDER of the series. The customer's edit belongs to the piece
 *     that was split off, and the remainder must keep the content it had. Taking the
 *     draft here would rewrite every other occurrence with an edit meant for one.
 *
 *   - a plan that creates nothing ("all", and — since first-occurrence
 *     "this and following" became a re-anchor instead of a delete+create — that case
 *     too) patches the very document the customer was editing. It is the only write
 *     the operation performs, so if it does not carry the draft, the edit is silently
 *     discarded: the schedule moves and the new message text does not.
 *
 * The distinction is structural — `newAction === 'create'` — not a scope name, so a
 * handler branch that changes its mind about creating a replacement drags this along
 * with it instead of leaving a stale scope list behind.
 */

export interface RecurringEditPatchPlan {
  newAction?: 'create' | 'none' | string | null;
  originalNewRRule?: string;
  originalDueDate: string | Date;
}

/**
 * The recurrence fields a plan writes onto the ORIGINAL document -- the one
 * definition every path that patches it uses (drag, delete, and edit).
 *
 * When the plan leaves the original with a rule, it is a series. When it does
 * not (a head left with ONE send, a series whose last survivor is its dueDate),
 * it is a one-off, and a one-off carries NO rule. That has to be written as an
 * explicit empty string: these patches travel as a JSON PATCH body, JSON has no
 * `undefined`, so `rruleset: undefined` was simply not sent and the server kept
 * the stale series rule (found live: an every-2-months COUNT=4 head, down to one
 * send, stored `isRecurring: false` beside its untouched COUNT=4 rule). Nothing
 * sends from it while `isRecurring` is false, but it is a one-off wearing a
 * live-looking series rule, and the next reader to trust `rruleset` over
 * `isRecurring` would resurrect the series. `''` is also what the compose editor
 * already uses for "not recurring".
 */
export function originalRecurrenceFields(
  plan: Pick<RecurringEditPatchPlan, 'originalNewRRule'>,
): { rruleset: string; isRecurring: boolean } {
  return plan.originalNewRRule
    ? { rruleset: plan.originalNewRRule, isRecurring: true }
    : { rruleset: '', isRecurring: false };
}

/**
 * @param plan     what the recurrence handler decided.
 * @param original the stored document being patched. Supplies `_id` and everything the
 *                 draft does not carry.
 * @param draft    the compose draft, i.e. the customer's edits.
 */
export function buildRecurringEditPatchFields<T extends { _id?: string }>(
  plan: RecurringEditPatchPlan,
  original: T,
  draft: Partial<T>,
): T & { rruleset: string; isRecurring: boolean; dueDate: Date; skipDayMappingFix: true } {
  const patchCarriesTheEdit = plan.newAction !== 'create';

  return {
    ...original,
    ...(patchCarriesTheEdit ? draft : {}),
    // IDENTITY is never the draft's to supply, only content is.
    //
    // `_id` decides which document is written at all. `clientIdempotencyKey` is the
    // stored document's own CREATE-dedup token, and the compose draft mints a fresh
    // one at dialog-open (BE#372/#231) precisely so an edit never inherits a stored
    // key — which means letting the draft win here would REPLACE a live series' token
    // with a throwaway on every edit. Re-anchoring a series in place exists to keep
    // the document, its id and its series-scoped key intact; overwriting the key on
    // the way would give back most of what was just protected.
    _id: original._id,
    clientIdempotencyKey: (original as any).clientIdempotencyKey,
    ...originalRecurrenceFields(plan),
    dueDate: new Date(plan.originalDueDate),
    skipDayMappingFix: true,
  } as T & { rruleset: string; isRecurring: boolean; dueDate: Date; skipDayMappingFix: true };
}
